import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Trash2, Newspaper, ArrowLeft, Loader2 } from "lucide-react";
import { listFavourites, removeFavourite, postTimeJump } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import AuthMenu from "@/components/AuthMenu";

export default function Favourites() {
  const { user, loading: authLoading, showSignIn } = useAuth();
  const navigate = useNavigate();
  const [favs, setFavs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    listFavourites()
      .then((list) => { setFavs(list); trackEvent("favourites_view", { count: list.length }); })
      .catch(() => toast.error("Could not load favourites"))
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  const openSnapshot = async (fav) => {
    setOpeningId(fav.snapshot_id);
    trackEvent("favourite_open", { snapshot_id: fav.snapshot_id });
    try {
      const data = await postTimeJump({ date: fav.date, region: fav.region });
      sessionStorage.setItem(`tc:${data.id}`, JSON.stringify(data));
      navigate(`/snapshot/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open snapshot");
    } finally {
      setOpeningId(null);
    }
  };

  const removeFav = async (snapshot_id) => {
    try {
      await removeFavourite(snapshot_id);
      setFavs((f) => f.filter((x) => x.snapshot_id !== snapshot_id));
      toast.success("Removed from favourites");
      trackEvent("favourite_remove", { snapshot_id });
    } catch {
      toast.error("Could not remove");
    }
  };

  return (
    <div className="min-h-screen w-full" data-testid="favourites-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
        <div className="flex items-center justify-between gap-3 mb-6">
          <Link
            to="/"
            data-testid="fav-back-btn"
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border-2 border-ink px-3 py-2 bg-paper-surface shadow-hard-sm hover:shadow-hard-hover"
            style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
          >
            <ArrowLeft size={14} /> Back
          </Link>
          <AuthMenu returnPath="/favourites" />
        </div>

        <header className="border-b-4 border-double border-ink pb-4 mb-8 text-center">
          <div className="ink-stamp red inline-block mb-2">Personal Archive</div>
          <h1 className="font-headline font-black uppercase leading-none tracking-tighter text-4xl sm:text-5xl">
            <Bookmark className="inline mr-2 -mt-2" size={40} /> My Favourites
          </h1>
          <div className="mt-3 font-mono uppercase tracking-[0.3em] text-xs text-ink-muted">
            The dates worth keeping
          </div>
        </header>

        {(authLoading || loading) && (
          <div className="text-center py-16" data-testid="fav-loading">
            <Loader2 size={28} className="animate-spin mx-auto text-press-red" />
            <div className="mt-3 font-mono uppercase tracking-widest text-xs text-ink-muted">
              Pulling the archive drawer…
            </div>
          </div>
        )}

        {!authLoading && !user && (
          <div className="text-center py-16 border-2 border-ink bg-paper-surface p-8" data-testid="fav-signed-out">
            <div className="font-headline text-2xl sm:text-3xl font-black uppercase mb-3">
              Sign in to keep your dates
            </div>
            <p className="font-body italic text-ink-muted max-w-lg mx-auto mb-6">
              Your favourite snapshots stay with you across devices — birthdays, anniversaries,
              the day the world stopped. All safely bookmarked.
            </p>
            <button
              data-testid="fav-signin-cta"
              onClick={() => showSignIn("/favourites")}
              className="inline-flex items-center gap-2 bg-press-red text-paper font-headline uppercase tracking-widest px-5 py-3 border-2 border-ink shadow-hard-sm hover:shadow-hard-hover"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              Sign in
            </button>
          </div>
        )}

        {!loading && user && favs.length === 0 && (
          <div className="text-center py-16 border-2 border-ink bg-paper-surface p-8" data-testid="fav-empty">
            <div className="font-headline text-2xl font-black uppercase mb-3">
              No favourites yet
            </div>
            <p className="font-body italic text-ink-muted max-w-lg mx-auto mb-6">
              Head back, pick a date, and tap the bookmark on any snapshot to keep it here.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-press-red text-paper font-headline uppercase tracking-widest px-5 py-3 border-2 border-ink shadow-hard-sm hover:shadow-hard-hover"
              style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
            >
              <Newspaper size={16} /> Jump to a date
            </Link>
          </div>
        )}

        {!loading && user && favs.length > 0 && (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="fav-list">
            {favs.map((f) => (
              <li
                key={f.snapshot_id}
                className="border-2 border-ink bg-paper-surface shadow-hard-sm p-4 sm:p-5 flex flex-col gap-2"
                data-testid={`fav-item-${f.snapshot_id}`}
              >
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                  <span>{f.region_flag} {f.region_name}</span>
                  <span>·</span>
                  <span>{f.long_date || f.date}</span>
                </div>
                <div className="font-headline text-lg sm:text-xl font-bold leading-tight line-clamp-3">
                  {f.headline || "Untitled snapshot"}
                </div>
                <div className="mt-auto pt-3 flex items-center gap-2">
                  <button
                    data-testid={`fav-open-${f.snapshot_id}`}
                    onClick={() => openSnapshot(f)}
                    disabled={openingId === f.snapshot_id}
                    className="inline-flex items-center gap-2 bg-press-red text-paper font-mono uppercase tracking-widest text-[11px] px-3 py-2 border-2 border-ink shadow-hard-sm hover:shadow-hard-hover disabled:opacity-70"
                    style={{ transition: "transform 150ms ease, box-shadow 150ms ease" }}
                  >
                    {openingId === f.snapshot_id ? <Loader2 size={12} className="animate-spin" /> : <Newspaper size={12} />}
                    Open snapshot
                  </button>
                  <button
                    data-testid={`fav-remove-${f.snapshot_id}`}
                    onClick={() => removeFav(f.snapshot_id)}
                    className="ml-auto inline-flex items-center gap-1 font-mono uppercase tracking-widest text-[10px] px-2 py-2 border-2 border-ink bg-paper-alt hover:bg-ink hover:text-paper"
                    aria-label="Remove"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
